import React, { useState, useEffect } from 'react';
import { Navigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';

const LocationSelector = ({ locations, onSelect, loading }) => {
  const [selectedId, setSelectedId] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedId) {
      onSelect(parseInt(selectedId));
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: 'linear-gradient(135deg, var(--text) 0%, var(--text) 50%, var(--text) 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '28px 32px',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '360px',
        width: '90%',
        textAlign: 'center',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ 
          fontSize: '36px', 
          marginBottom: '12px',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
        }}>📍</div>
        <h2 style={{ 
          margin: '0 0 6px 0', 
          color: 'var(--text)',
          fontSize: '20px',
          fontWeight: '700'
        }}>
          Wybierz lokalizację
        </h2>
        <p style={{ 
          color: 'var(--text-muted)', 
          marginBottom: '20px',
          fontSize: '13px',
          lineHeight: '1.4'
        }}>
          Wybierz sklep, w którym pracujesz
        </p>

        {loading ? (
          <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: '14px' }}>
            ⏳ Ładowanie...
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '14px',
                border: '2px solid var(--border)',
                borderRadius: '10px',
                marginBottom: '14px',
                backgroundColor: 'var(--bg)',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s ease',
                color: 'var(--text-secondary)',
                fontWeight: '500'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--info)';
                e.target.style.backgroundColor = '#fff';
                e.target.style.boxShadow = '0 0 0 3px rgba(13, 110, 253, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.backgroundColor = 'var(--bg)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">-- Wybierz --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.nazwa} {loc.miasto ? `(${loc.miasto})` : ''}
                </option>
              ))}
            </select>

            <button
              type="submit"
              disabled={!selectedId}
              style={{
                width: '100%',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#fff' ,
                background: selectedId 
                  ? 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)' 
                  : 'var(--text-muted)',
                borderRadius: '10px',
                cursor: selectedId ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                boxShadow: selectedId ? '0 4px 12px rgba(13, 110, 253, 0.3)' : 'none'
              }}
              onMouseOver={(e) => selectedId && (e.target.style.transform = 'translateY(-1px)')}
              onMouseOut={(e) => selectedId && (e.target.style.transform = 'translateY(0)')}
            >
              ✓ Kontynuuj
            </button>
          </form>
        )}

        <div style={{
          marginTop: '16px',
          paddingTop: '14px',
          borderTop: '1px solid var(--border)',
          fontSize: '11px',
          color: 'var(--text-muted)'
        }}>
          💡 Wybór zostanie zapamiętany
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, requireLocation = true, requireAdmin = false, requiredModule = null }) => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { selectedLocation, availableLocations, loading: locationLoading, changeLocation } = useLocation();
  const { hasPermission, loading: permLoading } = usePermissions();
  const routerLocation = useRouterLocation();

  // Reactive flag: czy haslo wygaslo (api.js interceptor wysyla event 'password-expired').
  // Gdy true, pomijamy WSZYSTKIE loadingi (location/permissions zawiesly w 403 i nie skoncza)
  // i renderujemy children (Layout) ktory otwiera modal zmiany hasla.
  const [pwdExpired, setPwdExpired] = useState(
    typeof window !== 'undefined' && window.__passwordExpiredHandled === true
  );
  useEffect(() => {
    const handler = () => setPwdExpired(true);
    window.addEventListener('password-expired', handler);
    return () => window.removeEventListener('password-expired', handler);
  }, []);

  // Pokazuj loader podczas sprawdzania autoryzacji
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Sprawdzanie autoryzacji...
      </div>
    );
  }

  // Jeśli nie jest zalogowany, przekieruj na stronę logowania z parametrem redirect
  if (!isAuthenticated) {
    const redirectPath = routerLocation.pathname !== '/login' ? routerLocation.pathname : '/';
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Jeśli wymagane uprawnienia admina i użytkownik nie jest adminem
  if (requireAdmin && !isAdmin) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚫</div>
        <h2 style={{ color: 'var(--danger)', marginBottom: '10px' }}>Brak dostępu</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
          Nie masz uprawnień do wyświetlenia tej strony.<br/>
          Ta sekcja jest dostępna tylko dla administratorów.
        </p>
        <a href="/" style={{
          padding: '12px 24px',
          border: '1px solid var(--info)', color: 'var(--info)' ,
          textDecoration: 'none',
          borderRadius: '8px',
          fontWeight: '600'
        }}>
          ← Wróć do strony głównej
        </a>
      </div>
    );
  }

  // Pokazuj loader podczas ładowania lokalizacji - ALE NIE jesli haslo wygaslo
  // (wtedy renderujemy children zeby Layout otworzyl modal zmiany hasla).
  if (locationLoading && !pwdExpired) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Ładowanie lokalizacji...
      </div>
    );
  }

  // Jeśli haslo wygaslo - renderuj Layout (z modalem) BEZ wymagania wyboru lokalizacji,
  // permissions itd. Po zmianie hasla ChangePasswordModal robi reload.
  if (pwdExpired) {
    return children;
  }

  // Jeśli wymagana lokalizacja i nie jest wybrana, pokaż ekran wyboru
  if (requireLocation && !selectedLocation) {
    return (
      <LocationSelector 
        locations={availableLocations}
        loading={locationLoading}
        onSelect={changeLocation}
      />
    );
  }

  // Sprawdź uprawnienia do modułu (jeśli wymagane)
  if (requiredModule && !isAdmin) {
    if (permLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px'
        }}>
          Sprawdzanie uprawnień...
        </div>
      );
    }

    // Uprawnienia sprawdzamy dla lokalizacji WYBRANEJ w topbarze (LocationContext),
    // a nie dla pierwszej lokalizacji zwroconej przez /permissions/my.
    // PermissionsContext.currentLocationId nie jest z nia synchronizowany
    // (changeLocation z tego kontekstu nie jest nigdzie wolane), wiec bez jawnego
    // 3. argumentu uzytkownik wielolokalizacyjny dostawal "Brak uprawnien" na
    // stronie, na ktora backend by go wpuscil.
    if (!hasPermission(requiredModule, 'dostep', selectedLocation?.id)) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚫</div>
          <h2 style={{ color: 'var(--danger)', marginBottom: '10px' }}>Brak uprawnień</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
            Nie masz uprawnień do tego modułu.<br/>
            Skontaktuj się z administratorem, aby uzyskać dostęp.
          </p>
          <a href="/" style={{
            padding: '12px 24px',
            border: '1px solid var(--info)', color: 'var(--info)' ,
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600'
          }}>
            ← Wróć do strony głównej
          </a>
        </div>
      );
    }
  }

  // Jeśli jest zalogowany i ma lokalizację, pokaż zawartość
  return children;
};

export default ProtectedRoute;
